import { isEqual } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import { useEffect, useState } from 'react';

/**
 * subscribes to changes of cursor position (if you pass withPosition = true) and text in the editor, if `getValue` returns a deep equal
 * value, it will return the previous value to keep referential equality
 */
export const useDerivedEditorValue = <Value, WithPosition extends boolean>({
  editor,
  getValue,
  // if this is true, the `getValue` callback will update the second param `position` `onDidChangeCursorPosition`
  withPosition,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  getValue: WithPosition extends true
    ? (text: string, position: monacoEditor.Position | undefined) => Value
    : (text: string, position: unknown) => Value;
  withPosition: WithPosition;
}) => {
  const [derivedValue, setDerivedValue] = useState(() =>
    getValue(editor.getValue(), editor.getPosition() ?? undefined),
  );
  // Set up a listener for model updates
  useEffect(() => {
    if (editor) {
      let nextUpdateTimeoutId: number | null = null;
      const updateValue = () => {
        // If we already have an update queued, clear it and queue the current one
        // With the latest event
        if (nextUpdateTimeoutId) clearTimeout(nextUpdateTimeoutId);

        // setTimeout so it does not block editor from updating
        nextUpdateTimeoutId = window.setTimeout(() => {
          nextUpdateTimeoutId = null;
          setDerivedValue((currentValue) => {
            const position = withPosition
              ? (editor.getPosition() ?? undefined)
              : undefined;
            const newValue = getValue(editor.getValue(), position);
            if (isEqual(newValue, currentValue)) {
              return currentValue;
            }
            return newValue;
          });
        });
      };
      updateValue();
      const disposables = [
        ...(withPosition
          ? [editor.onDidChangeCursorPosition(updateValue)]
          : []),
        editor.onDidChangeModel(updateValue),
        editor.onDidChangeModelContent(updateValue),
      ];

      return () => {
        disposables.forEach((disposable) => disposable?.dispose());
        if (nextUpdateTimeoutId) clearTimeout(nextUpdateTimeoutId);
      };
    }
  }, [editor, getValue, withPosition]);

  return derivedValue;
};
